import React, { useState, useEffect, useRef, useCallback } from 'react';
import HomeworkNumberEditor from './HomeworkNumberEditor';
import Modal from '../Common/Modal';
import { updateLesson, deleteLesson, createHomework, reorderHomeworks } from '../../services/adminService';
import { confirmDialog } from '../Common/confirm';
import toast from 'react-hot-toast';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Обёртка: делает карточку номера перетаскиваемой (тянуть за строку-шапку).
const SortableNumber = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: 'hw-' + props.homework.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0 : 1 };
  return <HomeworkNumberEditor {...props} dragRef={setNodeRef} dragStyle={style} rowProps={{ ...attributes, ...listeners }} />;
};

// Правая панель вкладки «Задания»: ДЗ-блок (урок без вебинара) — его номера в строку,
// каждый разворачивается. Номера сохраняются автоматически; «Опубликовать» делает ДЗ
// видимым ученику. Номера можно перетаскивать и дублировать.
const HomeworkNumbersPane = ({ lesson, list, webinars = [], onChanged, onNumberSaved, onLessonChanged, onDeleted, icon }) => {
  const [openIds, setOpenIds] = useState(() => new Set());
  const [items, setItems] = useState(list);
  const [title, setTitle] = useState(lesson.title || '');
  const [linkOpen, setLinkOpen] = useState(false);
  const [published, setPublished] = useState(!!lesson.isPublished);
  const pubRef = useRef(!!lesson.isPublished);
  const suppressClick = useRef(false); // гасим клик-тоггл сразу после перетаскивания

  useEffect(() => { setItems(list); }, [list]);
  useEffect(() => { setOpenIds(new Set()); setTitle(lesson.title || ''); setPublished(!!lesson.isPublished); pubRef.current = !!lesson.isPublished; }, [lesson.id]);
  useEffect(() => { setPublished(!!lesson.isPublished); pubRef.current = !!lesson.isPublished; }, [lesson.isPublished]);

  // Любое изменение содержимого опубликованного ДЗ снимает его с публикации (появляется облако).
  const maybeUnpublish = useCallback(async () => {
    if (!pubRef.current) return;
    pubRef.current = false; setPublished(false);
    try { await updateLesson(lesson.id, { isPublished: false }); } catch { /* тихо */ }
    onLessonChanged && onLessonChanged();
  }, [lesson.id, onLessonChanged]);

  const publish = async () => {
    try { await updateLesson(lesson.id, { isPublished: true }); setPublished(true); pubRef.current = true; toast.success('ДЗ опубликовано'); onLessonChanged && onLessonChanged(); }
    catch { toast.error('Ошибка публикации'); }
  };

  const toggle = useCallback((id) => { if (suppressClick.current) return; setOpenIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); }, []);
  const onNumberChanged = useCallback(async () => { await maybeUnpublish(); onChanged && onChanged(); }, [maybeUnpublish, onChanged]);

  // «Добавить номер» — сразу создаёт пустой номер-черновик и открывает его (без подтверждения).
  const addNew = async () => {
    try {
      const idx = items.length;
      const res = await createHomework({ lessonId: lesson.id, title: `№${idx + 1}`, answerType: 'single', description: '', maxScore: 1 });
      const newId = res.data?.id;
      await maybeUnpublish();
      onChanged && await onChanged();
      if (newId) setOpenIds(prev => new Set(prev).add(newId));
    } catch { toast.error('Не удалось добавить номер'); }
  };

  const onDuplicate = async (payload) => {
    try {
      await createHomework({ ...payload, lessonId: lesson.id });   // то же название, сразу под оригиналом
      toast.success('Номер продублирован');
      await maybeUnpublish();
      onChanged && await onChanged();
    } catch { toast.error('Не удалось дублировать'); }
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const handleDragEnd = (e) => {
    suppressClick.current = true; setTimeout(() => { suppressClick.current = false; }, 80);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setItems(prev => {
      const oldI = prev.findIndex(h => 'hw-' + h.id === active.id);
      const newI = prev.findIndex(h => 'hw-' + h.id === over.id);
      if (oldI < 0 || newI < 0) return prev;
      const next = arrayMove(prev, oldI, newI);
      reorderHomeworks(next.map(h => h.id))
        .then(() => { maybeUnpublish(); onChanged && onChanged(); })
        .catch(() => { toast.error('Не удалось сохранить порядок'); onChanged && onChanged(); });
      return next;
    });
  };

  const saveTitle = async () => {
    if (!title.trim() || title === lesson.title) return;
    try { await updateLesson(lesson.id, { title: title.trim() }); await maybeUnpublish(); onLessonChanged && onLessonChanged(); }
    catch { toast.error('Ошибка сохранения названия'); }
  };

  const setLink = async (webinarId) => {
    try {
      await updateLesson(lesson.id, { linkedLessonId: webinarId });
      toast.success(webinarId ? 'ДЗ привязано к уроку' : 'Связь убрана');
      setLinkOpen(false);
      await maybeUnpublish();
      onLessonChanged && onLessonChanged();
    } catch { toast.error('Ошибка'); }
  };

  const removeBlock = async () => {
    if (!(await confirmDialog({ title: 'Удалить ДЗ?', message: 'Блок и все его номера будут удалены.', confirmText: 'Удалить' }))) return;
    try { await deleteLesson(lesson.id); toast.success('ДЗ удалено'); onDeleted && onDeleted(); }
    catch { toast.error('Ошибка при удалении'); }
  };

  const count = items.length;
  const linkedTitle = lesson.linkedLessonId ? (webinars.find(w => w.id === lesson.linkedLessonId)?.title) : null;

  return (
    <div className="cc-editor">
      <div className="cc-editor-head">
        <div className={`cc-editor-ic${!published ? ' draft' : ''}`} title={!published ? 'Черновик — не опубликовано' : undefined}>
          {!published
            ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg>
            : (icon || <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <input className="cc-inline-title" value={title} onChange={e => setTitle(e.target.value)} onBlur={saveTitle} />
          <div className="cc-editor-sub" style={{ paddingLeft: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {count} {count === 1 ? 'номер' : count > 1 && count < 5 ? 'номера' : 'номеров'}
            {linkedTitle ? ` · привязано: ${linkedTitle}` : ' · без вебинара'}
          </div>
        </div>
        <button className={`cc-pub-btn ${published ? 'done' : 'active'}`} onClick={publish} disabled={published} title={published ? 'Опубликовано' : 'Опубликовать ДЗ'}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/><polyline points="9 13 12 10 15 13"/><line x1="12" y1="10" x2="12" y2="17"/></svg>
        </button>
        <button className={`cc-link-btn${lesson.linkedLessonId ? ' on' : ''}`} onClick={() => setLinkOpen(true)} title={lesson.linkedLessonId ? `Привязан: ${linkedTitle || 'вебинар'}` : 'Связать с вебинаром'}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
        </button>
        <button className="cc-del-btn" title="Удалить ДЗ" onClick={removeBlock}>
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
        </button>
      </div>

      <div className="cc-editor-body" style={{ gap: 8 }}>
        {count === 0 && (
          <div className="cc-empty-leaf" style={{ padding: '22px 8px' }}>В этом ДЗ пока нет номеров. Нажмите «Добавить номер».</div>
        )}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map(h => 'hw-' + h.id)} strategy={verticalListSortingStrategy}>
            {items.map((hw, idx) => (
              <SortableNumber
                key={hw.id}
                homework={hw}
                index={idx}
                showScore
                expanded={openIds.has(hw.id)}
                onToggle={toggle}
                onDirty={maybeUnpublish}
                onSaved={onNumberSaved}
                onDeleted={onNumberChanged}
                onDuplicate={onDuplicate}
              />
            ))}
          </SortableContext>
        </DndContext>

        <button className="cc-add-section" onClick={addNew} style={{ marginTop: 4 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Добавить номер
        </button>
      </div>

      <Modal isOpen={linkOpen} onClose={() => setLinkOpen(false)} title="Связать ДЗ с вебинаром">
        <p style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 14 }}>
          У ученика в этом ДЗ появится кнопка «К вебинару» на выбранный вебинар. Номера остаются в самом ДЗ.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 340, overflowY: 'auto' }}>
          {webinars.length === 0 && <div className="cc-empty-leaf">В курсе пока нет вебинаров</div>}
          {webinars.map(w => {
            const on = lesson.linkedLessonId === w.id;
            return (
              <button key={w.id} onClick={() => setLink(w.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', padding: '11px 13px', borderRadius: 11, cursor: 'pointer', fontFamily: 'inherit',
                  border: `1px solid ${on ? 'var(--primary)' : 'var(--border)'}`, background: on ? 'var(--primary-light)' : 'var(--card)', color: 'var(--text-1)' }}>
                <span style={{ width: 26, height: 26, borderRadius: 8, flexShrink: 0, background: 'var(--primary-light)', color: 'var(--primary-dark)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{w.title}</span>
                  <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-3)' }}>{w.section}</span>
                </span>
                {on && <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)' }}>✓</span>}
              </button>
            );
          })}
        </div>
        {lesson.linkedLessonId && (
          <div className="form-actions" style={{ marginTop: 14 }}>
            <button className="action-btn secondary" onClick={() => setLink(null)}>Убрать связь</button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default HomeworkNumbersPane;
